import {
  type CommonRequestFn,
  type CommonRequestFnWithoutPayload
} from "@marketplace/http";
import type { Object } from "ts-toolbelt";

import { ENDPOINTS } from "@/constants/endpoints";
import { httpClient } from "@/http";

import type {
  CreateBclProductRequestDto,
  CreateBclProductResponseDto,
  GetBclProductDetailResponseDto
} from "../dtos/bcl-product-dto";
import type { CreateProductAutomationRequestDto } from "../dtos/create-product-automation-request-dto";
import type { CreateProductAutomationResponseDto } from "../dtos/create-product-automation-response-dto";
import type { CreateProductRequestDto } from "../dtos/create-product-request-dto";
import type { CreateProductResponseDto } from "../dtos/create-product-response-dto";
import type {
  CreateCrmProductRequestDto,
  CreateCrmProductResponseDto,
  GetCrmProductDetailResponseDto,
  GetCrmProductHierarchyProductResponseDto
} from "../dtos/crm-product-dto";
import type { DeactivateProductAutomationRequestDto } from "../dtos/deactivate-product-automation-request-dto";
import type { DeleteProductPackageRequestDto } from "../dtos/delete-product-package-request-dto";
import type { DeleteProductRequestDto } from "../dtos/delete-product-request-dto";
import type { DeleteProductVariantRequestDto } from "../dtos/delete-product-variant-request-dto";
import type { EditProductDraftRequestDto } from "../dtos/edit-product-draft-request-dto";
import type { EditProductRequestDto } from "../dtos/edit-product-request-dto";
import type {
  CreateErfinaProductRequestDto,
  CreateErfinaProductResponseDto,
  GetErfinaProductDetailResponseDto
} from "../dtos/erfina-product-dto";
import type { GetIndustryCategoryListResponseDto } from "../dtos/get-industry-category-list-response-dto";
import type { GetIntegrationProductListRequestDto } from "../dtos/get-integration-product-list-request-dto";
import type { GetIntegrationProductListResponseDto } from "../dtos/get-integration-product-list-response-dto";
import type { GetProductAutomationDetailResponseDto } from "../dtos/get-product-automation-detail-response-dto";
import type { GetProductDetailRequestDto } from "../dtos/get-product-detail-request-dto";
import type { GetProductDetailResponseDto } from "../dtos/get-product-detail-response-dto";
import type { GetProductDraftDetailRequestDto } from "../dtos/get-product-draft-detail-request-dto";
import type { GetProductFeeTierListResponseDto } from "../dtos/get-product-fee-tier-list-response-dto";
import type { GetProductListRequestDto } from "../dtos/get-product-list-request-dto";
import type { GetProductListResponseDto } from "../dtos/get-product-list-response-dto";
import type { GetProductTypeListResponseDto } from "../dtos/get-product-type-list-response-dto";
import type { GetProductVariantListRequestDto } from "../dtos/get-product-variant-list-request-dto";
import type { GetProductVariantListResponseDto } from "../dtos/get-product-variant-list-response-dto";
import type { SetApprovalProductRequestDto } from "../dtos/set-approval-request-dto";
import type { SetPromoRequestDto } from "../dtos/set-promo-dto";
import type { SetPublishStatusRequestDto } from "../dtos/set-publish-status-request-dto";
import type {
  UpdatePlatformFeeRequestDto,
  UpdatePlatformFeeResponseDto
} from "../dtos/update-platform-fee-dto";

type ProductServiceContract = {
  getProductList: CommonRequestFn<
    GetProductListRequestDto,
    GetProductListResponseDto
  >;
  getProductDetail: CommonRequestFn<
    GetProductDetailRequestDto,
    GetProductDetailResponseDto
  >;
  getProductDraftList: CommonRequestFn<
    GetProductListRequestDto,
    GetProductListResponseDto
  >;
  getProductDraftDetail: CommonRequestFn<
    GetProductDraftDetailRequestDto,
    GetProductDetailResponseDto
  >;
  createProduct: CommonRequestFn<
    CreateProductRequestDto,
    CreateProductResponseDto
  >;
  editProduct: CommonRequestFn<EditProductRequestDto, CreateProductResponseDto>;
  deleteProduct: CommonRequestFn<DeleteProductRequestDto, { success: boolean }>;
  deleteProductDraft: CommonRequestFn<
    DeleteProductRequestDto,
    { success: boolean }
  >;
  deleteProductPackage: CommonRequestFn<
    DeleteProductPackageRequestDto,
    { success: boolean }
  >;
  deleteProductVariant: CommonRequestFn<
    DeleteProductVariantRequestDto,
    { success: boolean }
  >;
  getProductTypeList: CommonRequestFnWithoutPayload<GetProductTypeListResponseDto>;
  getProductFeeTierList: CommonRequestFnWithoutPayload<GetProductFeeTierListResponseDto>;
  getIndustryCategoryList: CommonRequestFnWithoutPayload<GetIndustryCategoryListResponseDto>;
  createProductDraft: CommonRequestFn<
    Object.Partial<CreateProductRequestDto, "deep">,
    CreateProductResponseDto
  >;
  editProductDraft: CommonRequestFn<
    EditProductDraftRequestDto,
    CreateProductResponseDto
  >;
  createProductAutomation: CommonRequestFn<
    CreateProductAutomationRequestDto,
    CreateProductAutomationResponseDto
  >;
  updateProductAutomation: CommonRequestFn<
    CreateProductAutomationRequestDto,
    CreateProductAutomationResponseDto
  >;
  getProductAutomationDetail: CommonRequestFn<
    { productId: string },
    GetProductAutomationDetailResponseDto
  >;
  deactivateProductAutomation: CommonRequestFn<
    DeactivateProductAutomationRequestDto,
    CreateProductAutomationResponseDto
  >;
  getProductVariantList: CommonRequestFn<
    GetProductVariantListRequestDto,
    GetProductVariantListResponseDto
  >;
  setPublishStatus: CommonRequestFn<
    SetPublishStatusRequestDto,
    { success: boolean }
  >;
  setApprovalProduct: CommonRequestFn<
    SetApprovalProductRequestDto,
    { success: boolean }
  >;
  getIntegrationProductList: CommonRequestFn<
    GetIntegrationProductListRequestDto,
    GetIntegrationProductListResponseDto
  >;
  getCrmProductDetail: CommonRequestFn<
    { productId: string },
    GetCrmProductDetailResponseDto
  >;
  createCrmProduct: CommonRequestFn<
    CreateCrmProductRequestDto,
    CreateCrmProductResponseDto
  >;
  updateCrmProduct: CommonRequestFn<
    CreateCrmProductRequestDto & { productId: string },
    CreateCrmProductResponseDto
  >;
  getCrmHierarchy: CommonRequestFn<
    { productId: string },
    GetCrmProductHierarchyProductResponseDto
  >;
  getBclProductDetail: CommonRequestFn<
    { productId: string },
    GetBclProductDetailResponseDto
  >;
  createBclProduct: CommonRequestFn<
    CreateBclProductRequestDto,
    CreateBclProductResponseDto
  >;
  updateBclProduct: CommonRequestFn<
    CreateBclProductRequestDto & { productId: string },
    CreateBclProductResponseDto
  >;
  getErfinaProductDetail: CommonRequestFn<
    { productId: string },
    GetErfinaProductDetailResponseDto
  >;
  createErfinaProduct: CommonRequestFn<
    CreateErfinaProductRequestDto,
    CreateErfinaProductResponseDto
  >;
  updateErfinaProduct: CommonRequestFn<
    CreateErfinaProductRequestDto & { productId: string },
    CreateErfinaProductResponseDto
  >;
  updatePlatformFee: CommonRequestFn<
    UpdatePlatformFeeRequestDto,
    UpdatePlatformFeeResponseDto
  >;
  setPromo: CommonRequestFn<SetPromoRequestDto, { success: boolean }>;
};

export const ProductService: ProductServiceContract = {
  getProductList: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.PRODUCT.LIST, {
        searchParams: payload,
        ...options
      })
      .json<GetProductListResponseDto>(),

  getProductDetail: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.PRODUCT.DETAIL(payload.slug), {
        ...options
      })
      .json<GetProductDetailResponseDto>(),

  getProductDraftList: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.PRODUCT.DRAFT.LIST, {
        searchParams: payload,
        ...options
      })
      .json<GetProductListResponseDto>(),

  getProductDraftDetail: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.PRODUCT.DRAFT.DETAIL(payload.id), {
        ...options
      })
      .json<GetProductDetailResponseDto>(),

  createProduct: async ({ payload, options }) =>
    httpClient
      .post(ENDPOINTS.PRODUCT.CREATE, {
        json: payload,
        ...options
      })
      .json<CreateProductResponseDto>(),

  editProduct: async ({ payload, options }) =>
    httpClient
      .put(ENDPOINTS.PRODUCT.DETAIL(payload.id), {
        json: payload,
        ...options
      })
      .json<CreateProductResponseDto>(),

  deleteProduct: async ({ payload, options }) =>
    httpClient
      .delete(ENDPOINTS.PRODUCT.DELETE(payload.productId), {
        ...options
      })
      .json<{ success: boolean }>(),

  deleteProductDraft: async ({ payload, options }) =>
    httpClient
      .delete(ENDPOINTS.PRODUCT.DRAFT.DELETE(payload.productId), {
        ...options
      })
      .json<{ success: boolean }>(),

  deleteProductPackage: async ({ payload, options }) =>
    httpClient
      .delete(ENDPOINTS.PRODUCT.DELETE_PACKAGE(payload.packageId), {
        ...options
      })
      .json<{ success: boolean }>(),

  deleteProductVariant: async ({ payload, options }) =>
    httpClient
      .delete(ENDPOINTS.PRODUCT.DELETE_VARIANT(payload.variantId), {
        ...options
      })
      .json<{ success: boolean }>(),

  getProductTypeList: async () =>
    httpClient
      .get(ENDPOINTS.PRODUCT.TYPE)
      .json<GetProductTypeListResponseDto>(),

  getProductFeeTierList: async () =>
    httpClient
      .get(ENDPOINTS.PRODUCT.FEE_TIER)
      .json<GetProductFeeTierListResponseDto>(),

  getIndustryCategoryList: async () =>
    httpClient
      .get(ENDPOINTS.PRODUCT.INDUSTRY.LIST)
      .json<GetIndustryCategoryListResponseDto>(),

  createProductDraft: async ({ payload, options }) =>
    httpClient
      .post(ENDPOINTS.PRODUCT.DRAFT.CREATE, {
        json: payload,
        ...options
      })
      .json<CreateProductResponseDto>(),

  editProductDraft: async ({ payload, options }) =>
    httpClient
      .put(ENDPOINTS.PRODUCT.DRAFT.DETAIL(payload.id), {
        json: payload,
        ...options
      })
      .json<CreateProductResponseDto>(),

  createProductAutomation: async ({ payload, options }) =>
    httpClient
      .post(ENDPOINTS.PRODUCT.AUTOMATION.CREATE, {
        json: payload,
        ...options
      })
      .json<CreateProductAutomationResponseDto>(),

  updateProductAutomation: async ({ payload, options }) =>
    httpClient
      .put(ENDPOINTS.PRODUCT.AUTOMATION.UPDATE, {
        json: payload,
        ...options
      })
      .json<CreateProductAutomationResponseDto>(),

  getProductAutomationDetail: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.PRODUCT.AUTOMATION.DETAIL(payload.productId), {
        ...options
      })
      .json<GetProductAutomationDetailResponseDto>(),

  deactivateProductAutomation: async ({ payload, options }) =>
    httpClient
      .put(ENDPOINTS.PRODUCT.AUTOMATION.DEACTIVE, {
        json: payload,
        ...options
      })
      .json<CreateProductAutomationResponseDto>(),

  getProductVariantList: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.PRODUCT.VARIANT_LIST(payload.productPackageId), {
        ...options
      })
      .json<GetProductVariantListResponseDto>(),

  setPublishStatus: async ({ payload, options }) =>
    httpClient
      .put(ENDPOINTS.PRODUCT.SET_PUBLISH_STATUS(payload.productId), {
        json: { is_publish: payload.is_publish },
        ...options
      })
      .json<{ success: boolean }>(),

  setApprovalProduct: async ({ payload, options }) =>
    httpClient
      .put(ENDPOINTS.PRODUCT.APPROVAL(payload.productId), {
        json: { product_status: payload.product_status },
        ...options
      })
      .json<{ success: boolean }>(),

  getIntegrationProductList: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.PRODUCT.INTEGRATION.LIST, {
        searchParams: payload,
        ...options
      })
      .json<GetIntegrationProductListResponseDto>(),

  getCrmProductDetail: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.PRODUCT.INTEGRATION.CRM.GET_DETAIL(payload.productId), {
        ...options
      })
      .json<GetCrmProductDetailResponseDto>(),

  createCrmProduct: async ({ payload, options }) =>
    httpClient
      .post(ENDPOINTS.PRODUCT.INTEGRATION.CRM.CREATE, {
        json: payload,
        ...options
      })
      .json<CreateCrmProductResponseDto>(),

  updateCrmProduct: async ({ payload, options }) => {
    const { productId, ...body } = payload;
    return httpClient
      .put(ENDPOINTS.PRODUCT.INTEGRATION.CRM.UPDATE(productId), {
        json: body,
        ...options
      })
      .json<CreateCrmProductResponseDto>();
  },

  getCrmHierarchy: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.PRODUCT.INTEGRATION.CRM.GET_HIERARCHY(payload.productId), {
        ...options
      })
      .json<GetCrmProductHierarchyProductResponseDto>(),

  getBclProductDetail: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.PRODUCT.INTEGRATION.BCL.GET_DETAIL(payload.productId), {
        ...options
      })
      .json<GetBclProductDetailResponseDto>(),

  createBclProduct: async ({ payload, options }) =>
    httpClient
      .post(ENDPOINTS.PRODUCT.INTEGRATION.BCL.CREATE, {
        json: payload,
        ...options
      })
      .json<CreateBclProductResponseDto>(),

  updateBclProduct: async ({ payload, options }) => {
    const { productId, ...body } = payload;
    return httpClient
      .put(ENDPOINTS.PRODUCT.INTEGRATION.BCL.UPDATE(productId), {
        json: body,
        ...options
      })
      .json<CreateBclProductResponseDto>();
  },

  getErfinaProductDetail: async ({ payload, options }) =>
    httpClient
      .get(ENDPOINTS.PRODUCT.INTEGRATION.ERFINA.GET_DETAIL(payload.productId), {
        ...options
      })
      .json<GetErfinaProductDetailResponseDto>(),

  createErfinaProduct: async ({ payload, options }) =>
    httpClient
      .post(ENDPOINTS.PRODUCT.INTEGRATION.ERFINA.CREATE, {
        json: payload,
        ...options
      })
      .json<CreateErfinaProductResponseDto>(),

  updateErfinaProduct: async ({ payload, options }) => {
    const { productId, ...body } = payload;
    return httpClient
      .put(ENDPOINTS.PRODUCT.INTEGRATION.ERFINA.UPDATE(productId), {
        json: body,
        ...options
      })
      .json<CreateErfinaProductResponseDto>();
  },

  updatePlatformFee: async ({ payload, options }) =>
    httpClient
      .put(ENDPOINTS.PRODUCT.PLATFORM_FEE, {
        json: payload,
        ...options
      })
      .json<UpdatePlatformFeeResponseDto>(),

  setPromo: async ({ payload, options }) =>
    httpClient
      .put(ENDPOINTS.PRODUCT.SET_PROMO(payload.productId), {
        json: { is_promo_available: payload.is_promo_available },
        ...options
      })
      .json<{ success: boolean }>()
};
